import moment from 'moment'

/**
 * Maps an "All" metric type to the corresponding metric in the performance reporting GraphQL response.
 *
 * If not an "All" type, returns the metric as is.
 */
const getMetric = metric => {
  switch (metric) {
    case 'netMarginAll':
      return 'netMargin'
    case 'netProfitAll':
      return 'netProfit'
    case 'businessProfitAll':
      return 'businessProfit'
    default:
      return metric
  }
}

/**
 * Formats a performance report data date into a readable string.
 *
 * Defaults to a long-form format.
 * Can pass a moment format string to override (e.g. the Key Performance Metrics report uses a shorter form, with the year conditionally included)
 */
const formatDate = (date, format) => {
  return moment.utc(date).format(format || 'dddd Do MMMM YYYY')
}

/**
 * Formats a performance report data date into recognisable date field in excel/spreadsheet client
 * ISO8601 date format
 */
const formatDateToISO = date => {
  return moment.utc(date).toISOString()
}

/**
 * Return child group data for a given groupId
 */
const getGroupData = (groups, groupId) => {
  let result
  for (let group of groups) {
    result = group.childGroups.find(child => child.id === groupId)
    if (!!result) return result
  }
}

/**
 * Calculates the number of selected stations or groups from an array of station and/or group ids.
 */
const calculateSelectedStationsAndGroupsAmount = (stationOrGroup = 'stations', selectedStationsAndGroups) => {
  if(stationOrGroup === 'stations') {
    return selectedStationsAndGroups.filter(item => item.includes('stat-')).length
  } else {
    return selectedStationsAndGroups.filter(item => !item.includes('stat-')).length
  }
}

/**
 * Return groups relevant to a search term
 */
function searchGroupsData(groupsData, searchTerm) {
  const lowerSearch = searchTerm.toLowerCase()

  return groupsData
    .map(group => {
      const filteredChildGroups = group.childGroups.filter(child =>
        child.name.toLowerCase().includes(lowerSearch)
      )

      if (filteredChildGroups.length > 0) {
        return {
          id: group.id,
          name: group.name,
          childGroups: filteredChildGroups,
        }
      }

      return null
    })
    .filter(Boolean)
}

const getTableCellStickyStyle = (stickyConfig, isHeader = true, rowOdd = false) => {
  if (!stickyConfig) return null

  const baseStickyStyle = {
    position: 'sticky',
    left: `${stickyConfig.left}px`,
    zIndex: stickyConfig.zIndex || 1,
    minWidth: `${stickyConfig.width}px`,
    width: `${stickyConfig.width}px`,
    maxWidth: `${stickyConfig.width}px`,
    backgroundColor: '#ffffff',
    ...(stickyConfig.isLast && {
      boxShadow: '4px 0 6px 1px rgba(0, 0, 0, 0.1)',
      clipPath: 'inset(0 -11px 0 0)' // Prevents box shadow from showing on the top of the cell
    })
  }

  if (isHeader) {
    return {
      ...baseStickyStyle,
      top: 0,
      zIndex: 3, // sticky header cells must layer above sticky body cells (z-index 1) and the regular headers (z-index 2)
    }
  }

  let backgroundColor = '#ffffff'
  if (rowOdd) backgroundColor = '#f3fff6' // matches $green-vlight in _variables.scss

  return {
    ...baseStickyStyle,
    backgroundColor,
    marginRight: 0
  }
}

export { getMetric, formatDate, formatDateToISO, getGroupData, calculateSelectedStationsAndGroupsAmount, searchGroupsData, getTableCellStickyStyle }
